import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import { CurrencyDollar, ArrowsClockwise } from '@phosphor-icons/react';
import api from '../../services/api';
import { formatDate, formatCurrency } from '../../lib/utils';

export const RemittanceTab = ({ remittanceLoading, remittanceHistory, onRefresh }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [content, setContent] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleProcess = async () => {
    if (!content.trim()) { toast.error('Please paste 835 remittance content'); return; }
    setProcessing(true);
    try {
      const res = await api.post('/remittance/process', { edi_content: content, auto_post: true });
      setResult(res.data);
      if (res.data.success) toast.success(`Processed ${res.data.summary?.total_claims || 0} claims, posted ${res.data.summary?.posted || 0}`);
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to process remittance');
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerateMock = async () => {
    try {
      const res = await api.post('/remittance/generate-mock', {
        claims: [
          { claim_number: 'DEMO001', billed_amount: 150.00, paid_amount: 120.00 },
          { claim_number: 'DEMO002', billed_amount: 250.00, paid_amount: 200.00 },
          { claim_number: 'DEMO003', billed_amount: 100.00, paid_amount: 80.00 },
        ],
      });
      setContent(res.data.edi_content);
      toast.success('Mock 835 generated - click Process to test');
    } catch (error) {
      toast.error('Failed to generate mock 835');
    }
  };

  if (remittanceLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">Remittance History</h3>
        <Button onClick={() => setModalOpen(true)} className="bg-indigo-900 hover:bg-indigo-800" data-testid="process-remittance-btn">
          <CurrencyDollar size={16} className="mr-2" />
          Process 835
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="pt-6">
          {remittanceHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <CurrencyDollar size={48} className="mx-auto mb-3" />
              <p>No remittance postings yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Claims</TableHead>
                  <TableHead>Total Paid</TableHead>
                  <TableHead>Matched</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {remittanceHistory.map((posting) => (
                  <TableRow key={posting.id}>
                    <TableCell className="text-sm">{formatDate(posting.created_at)}</TableCell>
                    <TableCell>{posting.total_claims || 0}</TableCell>
                    <TableCell className="font-medium text-emerald-700">{formatCurrency(posting.total_paid || 0)}</TableCell>
                    <TableCell>{posting.matched || 0}/{posting.total_claims || 0}</TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-100 text-emerald-700">Posted</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Process Remittance Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Process 835 Remittance</DialogTitle>
            <DialogDescription>Paste 835 Electronic Remittance Advice to auto-post payments</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>835 EDI Content</Label>
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Paste 835 remittance content here..." className="h-48 font-mono text-xs" data-testid="remittance-content-input" />
            </div>
            <Button variant="outline" size="sm" onClick={handleGenerateMock}>Generate Mock 835 for Testing</Button>
            {result && (
              <div className={`p-4 rounded-lg border ${result.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <h4 className="font-semibold mb-2">{result.success ? 'Processing Complete' : 'Processing Failed'}</h4>
                {result.summary && (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Total Claims: {result.summary.total_claims}</div>
                    <div>Matched: {result.summary.matched}</div>
                    <div className="text-emerald-600">Posted: {result.summary.posted}</div>
                    <div className="text-amber-600">Unmatched: {result.summary.unmatched}</div>
                    <div className="col-span-2 font-semibold">Total Paid: {formatCurrency(result.summary.total_paid || 0)}</div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Close</Button>
            <Button onClick={handleProcess} disabled={processing || !content.trim()} className="bg-indigo-900 hover:bg-indigo-800" data-testid="process-remittance-submit">
              {processing ? <ArrowsClockwise size={18} className="mr-2 animate-spin" /> : <CurrencyDollar size={18} className="mr-2" />}
              Process & Post Payments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
